import { Link } from "react-router-dom";
import { errorKind, type ErrorKind } from "@/api/errors";
import type { ApiError } from "@/types";

interface Props {
  error: ApiError;
  what: string; // e.g. "the webhook log"
  onRetry?: () => void;
  /** Shown for a 404 instead of the generic text, e.g. when a feature isn't deployed yet. */
  notFound?: string;
}

/** One fallback card for any failed query: network drop, 429, bad scopes, missing record. */
export function QueryError({ error, what, onRetry, notFound }: Props) {
  const kind = errorKind(error);

  if (kind === "not_found" || kind === "unavailable") {
    return <div className="card muted">{notFound ?? `Couldn't find ${what}.`}</div>;
  }

  const text: Record<Exclude<ErrorKind, "not_found" | "unavailable">, string> = {
    network: `Can't reach VetGuard to load ${what}. Check your connection, then retry.`,
    rate_limited: `ezyVet's limit of 60 calls a minute has been reached.${
      error.retry_after_seconds ? ` Try again in ${Math.ceil(error.retry_after_seconds)}s.` : " Try again in a minute."
    }`,
    auth: `ezyVet refused the request: ${error.message}. The API credentials may be missing a permission (scope).`,
    other: `Failed to load ${what}: ${error.message}`,
  };

  return (
    <div className={`card callout ${kind === "rate_limited" ? "warning" : "error"}`} role="alert">
      <p style={{ margin: 0 }}>{text[kind]}</p>
      <div className="row" style={{ marginTop: 12 }}>
        {onRetry && (
          <button type="button" className="btn btn-sm" onClick={onRetry}>
            Retry
          </button>
        )}
        {kind === "auth" && (
          <Link to="/settings/integrations/ezyvet" className="btn btn-sm">
            Check ezyVet settings
          </Link>
        )}
      </div>
    </div>
  );
}
